import { useState, useEffect, type FormEvent } from 'react';
import { Modal } from './Modal';
import type { Column } from '../../types/board';

interface EditColumnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, color: string) => void;
  column: Column | null;
  isLoading?: boolean;
}

const COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#6b7280', // gray
];

export function EditColumnModal({
  isOpen,
  onClose,
  onSave,
  column,
  isLoading = false,
}: EditColumnModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[0]);

  useEffect(() => {
    if (column) {
      setName(column.name);
      setColor(column.color || COLORS[0]);
    }
  }, [column]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && !isLoading) {
      onSave(name.trim(), color);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Column">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="columnName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Column Name
          </label>
          <input
            type="text"
            id="columnName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter column name"
            autoFocus
            disabled={isLoading}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Color
          </label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`w-8 h-8 rounded-lg border-2 transition-all ${
                  color === c
                    ? 'border-gray-900 dark:border-white scale-110'
                    : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || isLoading}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
